import { createFileRoute, Link } from "@tanstack/react-router";
import { Search as SearchIcon, ArrowUpRight } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { KeywordHubLinks } from "@/components/site/KeywordHubLinks";
import { searchSite, popularSearches } from "@/lib/search-index";
import { OG_ABOUT } from "@/lib/og-images";

const TITLE = "Search — Raj Shamani";
const DESC =
  "Search everything on rajshamani.life — age, net worth, family, Shamani Industries, Build Don't Talk and every Figuring Out podcast episode.";
const URL = "https://rajshamani.life/search";

export const Route = createFileRoute("/search")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q.slice(0, 120) : "",
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      // Search result pages should never be indexed, but links must stay crawlable.
      { name: "robots", content: "noindex, follow", key: "robots" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:image", content: OG_ABOUT, key: "og:image" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG_ABOUT, key: "twitter:image" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: SearchPage,
});

function SearchPage() {
  const { q } = Route.useSearch();
  // Computed during SSR — results are present in the initial HTML.
  const results = searchSite(q);

  return (
    <div>
      <PageBreadcrumb current="Search" />

      <section className="mx-auto max-w-5xl px-6 pt-8 pb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-6">
          Search Raj Shamani
        </h1>

        {/* Native GET form: works without JavaScript and re-renders on the server */}
        <form method="get" action="/search" role="search" className="max-w-xl">
          <label htmlFor="site-search" className="sr-only">
            Search this site
          </label>
          <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 focus-within:border-gold/60 transition">
            <SearchIcon size={18} className="text-gold shrink-0" />
            <input
              id="site-search"
              name="q"
              type="search"
              defaultValue={q}
              key={q}
              placeholder="Try “net worth”, “Dhoni”, “Build Don't Talk”…"
              className="w-full bg-transparent outline-none text-base placeholder:text-muted-foreground"
            />
            <button
              type="submit"
              className="text-sm font-medium text-gold hover:underline shrink-0"
            >
              Search
            </button>
          </div>
        </form>

        {q ? (
          <p className="mt-4 text-sm text-muted-foreground">
            {results.length} result{results.length === 1 ? "" : "s"} for{" "}
            <span className="text-foreground font-medium">“{q}”</span>
          </p>
        ) : (
          <div className="mt-6">
            <p className="text-sm text-muted-foreground mb-3">
              Popular searches
            </p>
            <ul className="flex flex-wrap gap-2">
              {popularSearches.map((s) => (
                <li key={s}>
                  <Link
                    to="/search"
                    search={{ q: s }}
                    className="inline-flex rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:border-gold/50 transition"
                  >
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {q && (
        <section className="mx-auto max-w-5xl px-6 pb-8">
          {results.length === 0 ? (
            <p className="text-muted-foreground">
              Nothing matched that. Try a broader term like{" "}
              <Link
                to="/search"
                search={{ q: "podcast" }}
                className="text-gold hover:underline"
              >
                podcast
              </Link>{" "}
              or browse the{" "}
              <Link to="/podcast" className="text-gold hover:underline">
                full episode list
              </Link>
              .
            </p>
          ) : (
            <ul className="grid gap-3">
              {results.map((r) => (
                <li key={`${r.to}${r.params?.slug ?? ""}`}>
                  <Link
                    to={r.to}
                    params={r.params as never}
                    className="group flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-4 hover:border-gold/50 transition"
                  >
                    <span className="flex-1">
                      <span className="text-[11px] uppercase tracking-wide text-gold">
                        {r.kind}
                      </span>
                      <span className="block font-semibold mt-0.5 inline-flex items-center gap-1.5">
                        {r.title}
                        <ArrowUpRight
                          size={13}
                          className="text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition"
                        />
                      </span>
                      <span className="block text-sm text-muted-foreground leading-relaxed mt-1">
                        {r.desc}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <KeywordHubLinks heading="Explore the most-searched topics" />
    </div>
  );
}
